// ==========================================================================================
// NOMBRE DEL ARCHIVO: src/components/PaymentPortal.jsx
// DESCRIPCIÓN: Portal de pagos para clientes (Wompi + Comprobante Manual)
// [Task 9] Integración Automatizada
// ==========================================================================================

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Button, Card, CardContent,
  Divider, TextField, Stack, Paper, Grid
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function PaymentPortal() {
  const [status, setStatus] = useState('loading'); // loading, ready, success, error, submitting
  const [data, setData] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [amount, setAmount] = useState('');
  const [file, setFile] = useState(null);
  
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('Enlace de pago no válido (falta token)');
      return;
    }
    loadPaymentData();
  }, [token]);

  const loadPaymentData = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/payments/portal/${token}`);
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || 'Error al cargar datos');
      
      setData(json);
      setAmount(json.balanceDue); // Por defecto pagar el saldo
      setStatus('ready');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!file) return alert('Sube el comprobante');
    setStatus('submitting');

    const formData = new FormData();
    formData.append('proof', file);
    formData.append('amount', amount);

    try {
      const res = await fetch(`${API_BASE}/api/payments/portal/${token}/submit`, {
        method: 'POST',
        body: formData
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error);
      setStatus('success');
    } catch (err) {
      setStatus('ready');
      alert(err.message);
    }
  };

  const handleWompiPay = () => {
    // [Task 9] Integración con Wompi Widget
    // Para simplificar, abrimos el checkout de Wompi
    // En producción, aquí se usaría el Script de Wompi o redirección directa
    const publicKey = "pub_prod_XXXXX"; // Esto debería venir del backend
    const reference = data.quoteNumber;
    const amountInCents = Math.round(amount) * 100;
    
    const checkoutUrl = `https://checkout.wompi.co/p/?public-key=${publicKey}&currency=COP&amount-in-cents=${amountInCents}&reference=${reference}&redirect-url=https://app.inflablesdasilva.com/pagar?status=success`;
    
    window.location.href = checkoutUrl;
  };

  const formatCurrency = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(n);

  if (status === 'loading') return <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 10 }}><CircularProgress /><Typography sx={{ mt: 2 }}>Cargando portal de pagos...</Typography></Box>;
  if (status === 'error') return <Box sx={{ p: 4 }}><Alert severity="error">{errorMsg}</Alert></Box>;
  if (status === 'success') return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
      <Typography variant="h4" gutterBottom>¡Comprobante Enviado!</Typography>
      <Typography>Hemos recibido tu reporte de pago. Un asesor lo validará en breve.</Typography>
      <Button variant="contained" sx={{ mt: 4 }} onClick={() => window.location.href = '/'}>Cerrar</Button>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom align="center" sx={{ fontWeight: 800, color: '#1e293b' }}>Portal de Pagos</Typography>
      <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>Inflables Da Silva & FunPark</Typography>

      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent>
          <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700 }}>RESUMEN DE COTIZACIÓN</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, mt: 1 }}>{data.quoteNumber}</Typography>
          <Typography variant="body1">{data.clientName}</Typography>
          
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography color="text.secondary">Total Evento:</Typography>
              <Typography fontWeight={700}>{formatCurrency(data.total)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography color="text.secondary">Pagado a la fecha:</Typography>
              <Typography color="success.main" fontWeight={700}>{formatCurrency(data.paidAmount)}</Typography>
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
              <Typography variant="h6" fontWeight={800}>Saldo Pendiente:</Typography>
              <Typography variant="h6" color="primary" fontWeight={800}>{formatCurrency(data.balanceDue)}</Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="h6" gutterBottom sx={{ mt: 4, fontWeight: 700 }}>¿Cómo deseas pagar?</Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Paper 
            elevation={0} 
            sx={{ p: 3, height: '100%', border: '2px solid #6366f1', borderRadius: 3, cursor: 'pointer', bgcolor: 'rgba(99, 102, 241, 0.04)', '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.08)' } }}
            onClick={handleWompiPay}
          >
            <CreditCardIcon sx={{ fontSize: 40, color: '#6366f1', mb: 1 }} />
            <Typography variant="h6" fontWeight={700}>Pago Online</Typography>
            <Typography variant="caption" color="text.secondary">Tarjeta, PSE, Bancolombia. Instantáneo.</Typography>
            <Button variant="contained" fullWidth sx={{ mt: 2, bgcolor: '#6366f1' }}>Pagar Ahora</Button>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Paper 
            elevation={0} 
            sx={{ p: 3, height: '100%', border: '1px solid #e2e8f0', borderRadius: 3, cursor: 'pointer', '&:hover': { border: '1px solid #6366f1' } }}
            onClick={() => document.getElementById('manual-section').scrollIntoView({ behavior: 'smooth' })}
          >
            <CloudUploadIcon sx={{ fontSize: 40, color: '#64748b', mb: 1 }} />
            <Typography variant="h6" fontWeight={700}>Transferencia</Typography>
            <Typography variant="caption" color="text.secondary">Nequi, Daviplata, Bancolombia. Adjunta comprobante.</Typography>
            <Button variant="outlined" fullWidth sx={{ mt: 2 }}>Subir Foto</Button>
          </Paper>
        </Grid>
      </Grid>

      <Box id="manual-section" sx={{ mt: 6, p: 3, border: '1px solid #e2e8f0', borderRadius: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight={700}>Reportar Pago Manual</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Si ya hiciste la transferencia, adjunta el pantallazo aquí.</Typography>
        
        <form onSubmit={handleManualSubmit}>
          <Stack spacing={3}>
            <TextField 
              fullWidth 
              label="Monto Pagado" 
              type="number" 
              value={amount} 
              onChange={e => setAmount(e.target.value)}
              InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
            />
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              fullWidth
            >
              {file ? file.name : 'Elegir Imagen / PDF'}
              <input type="file" hidden accept="image/*,application/pdf" onChange={e => setFile(e.target.files[0])} />
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              size="large" 
              disabled={status === 'submitting'}
              fullWidth
            >
              {status === 'submitting' ? <CircularProgress size={24} /> : 'Enviar Comprobante'}
            </Button>
          </Stack>
        </form>
      </Box>

      <Typography variant="caption" display="block" align="center" sx={{ mt: 6, color: '#94a3b8' }}>
        Transacciones protegidas por encriptación SSL de 256 bits.
      </Typography>
    </Box>
  );
}
